import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { storageFor } from 'ember-local-storage';

/**
 * Column Preferences Service
 *
 * Manages user preferences for column visibility.
 * Preferences are stored per-classgroup in localStorage.
 */
export default class ColumnPreferencesService extends Service {
  @storageFor('column-preferences') storage;

  // Tracked version to force reactivity when preferences change
  @tracked _version = 0;

  /**
   * Get visible column names for a specific classgroup
   * @param {string} classGroupId - The classgroup ID
   * @returns {Array|null} - Array of visible column names or null if no saved preferences
   */
  getVisibleColumns(classGroupId) {
    // Access _version to create a tracked dependency
    // eslint-disable-next-line no-unused-vars
    const version = this._version;
    const prefs = this.storage.get('visibleColumns') || {};
    return prefs[classGroupId] || null;
  }

  /**
   * Update visible columns for a classgroup
   * @param {string} classGroupId - The classgroup ID
   * @param {Array} visibleColumns - Array of visible column names
   */
  updateVisibleColumns(classGroupId, visibleColumns) {
    const prefs = this.storage.get('visibleColumns') || {};
    prefs[classGroupId] = visibleColumns;
    this.storage.set('visibleColumns', { ...prefs });
    // Increment version to trigger reactivity
    this._version++;
  }

  /**
   * Toggle column visibility for a classgroup
   * @param {string} classGroupId - The classgroup ID
   * @param {string} columnName - The column name to toggle
   * @param {Array} defaultColumns - Default column names if no saved preferences exist
   */
  toggleColumn(classGroupId, columnName, defaultColumns) {
    let visible = this.getVisibleColumns(classGroupId);

    // Initialize from defaults if no saved preferences
    if (!visible) {
      visible = [...defaultColumns];
    }

    if (visible.includes(columnName)) {
      // Hide column - remove from visible
      visible = visible.filter(n => n !== columnName);
    } else {
      // Show column - add to visible (at end)
      visible = [...visible, columnName];
    }

    this.updateVisibleColumns(classGroupId, visible);
  }
}
